import mockMusicLinksData from "../data/mockData/musicLinksData";
import mockMusicLinkUrlPartsData from "../data/mockData/musicLinkUrlPartsData";
import mlPremadeColorsData from "../data/mockData/mlPremadeColorsData";
import {
  getActiveDemoMusicLinkSeedRows,
  getDemoMusicLinkRows,
} from "./demoMusicLinkSessionStore";

const STORE_KEYS = [
  "amazonmusic",
  "applemusic",
  "beatport",
  "deezer",
  "junodownload",
  "soundcloud",
  "spotify",
  "tidal",
  "traxsource",
  "youtube",
  "anghami",
  "bandcamp",
  "boomplay",
  "iheartradio",
  "jiosaavn",
  "sevendigital",
];

function mergeById(baseRows, extraRows) {
  const map = new Map();
  (baseRows || []).forEach(row => {
    if (row && row.id != null) {
      map.set(String(row.id), row);
    }
  });
  (extraRows || []).forEach(row => {
    if (row && row.id != null) {
      map.set(String(row.id), { ...(map.get(String(row.id)) || {}), ...row });
    }
  });
  return Array.from(map.values());
}

export function parseMusicLinkPublicPath(url) {
  const match = String(url || "").match(/\/l\/([^/]+)\/?([^/]*)\/?$/i);
  if (!match) {
    return { slug: "", urlPart2: "" };
  }
  return {
    slug: match[1] || "",
    urlPart2: (match[2] || "").replace(/\/$/, ""),
  };
}

export function resolveUrlPartFromSlug(slug) {
  const normalized = String(slug || "").trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  const hit = mockMusicLinkUrlPartsData.find(
    part => String(part.part || "").trim().toLowerCase() === normalized,
  );
  if (hit) {
    return hit;
  }
  return {
    id: `slug-${normalized}`,
    part: slug,
    can_be_deleted: true,
    owner: "Demo",
  };
}

export function enrichMusicLinkListRow(row) {
  if (!row) {
    return row;
  }
  const next = { ...row };
  if (!next.url_part2 || !next.url_part1) {
    const { slug, urlPart2 } = parseMusicLinkPublicPath(next.url);
    const urlPart = resolveUrlPartFromSlug(slug);
    next.url_part2 = next.url_part2 || urlPart2;
    next.url_part1 = next.url_part1 ?? urlPart?.id;
    next.url_part1_name = next.url_part1_name || urlPart?.part || slug;
  }
  if (!next.image_small && next.image) {
    next.image_small = next.image;
  }
  if (!next.image_big && next.image) {
    next.image_big = next.image;
  }
  return next;
}

/** Resolve header/button/player colors from custom, premade id, or flattened row fields. */
export function resolveMusicLinkColorSource(row) {
  const colorScheme = Number(row?.color_scheme) || 1;
  const custom =
    row?.custom_color && typeof row.custom_color === "object" ? row.custom_color : null;

  if (colorScheme === 2 && custom) {
    return custom;
  }

  if (row?.premade_color != null && row.premade_color !== "") {
    const premade = mlPremadeColorsData.find(
      item => String(item.id) === String(row.premade_color),
    );
    if (premade) {
      return premade;
    }
  }

  const hasFlatColors =
    row?.header_color ||
    row?.title_color ||
    row?.button_color ||
    row?.description_color ||
    row?.player_controls_color;

  if (hasFlatColors) {
    return row;
  }

  return mlPremadeColorsData[0] || {};
}

/** Flatten premade/custom palette onto the row so session storage and landing page stay in sync. */
export function materializeMusicLinkColors(payload, id) {
  const colorScheme = Number(payload?.color_scheme) || 1;
  const custom =
    payload?.custom_color && typeof payload.custom_color === "object"
      ? { ...payload.custom_color }
      : null;

  if (colorScheme === 2 && custom) {
    const customId = custom.id ?? (id != null ? `custom-ml-${id}` : undefined);
    const headerColorScheme =
      Number(custom.header_color_scheme) ||
      (custom.header_gradient ? 2 : 1);

    return {
      color_scheme: 2,
      premade_color: null,
      custom_color: {
        ...custom,
        ...(customId != null ? { id: customId } : {}),
        header_color_scheme: headerColorScheme,
      },
      header_color_scheme: headerColorScheme,
      header_color: custom.header_color,
      header_gradient: custom.header_gradient ?? null,
      title_color: custom.title_color,
      description_color: custom.description_color,
      button_color: custom.button_color,
      button_text_color: custom.button_text_color,
      player_controls_color: custom.player_controls_color,
    };
  }

  if (colorScheme === 1 && payload?.premade_color != null && payload.premade_color !== "") {
    const premade = mlPremadeColorsData.find(
      item => String(item.id) === String(payload.premade_color),
    );
    if (premade) {
      return {
        color_scheme: 1,
        premade_color: premade.id,
        custom_color: null,
        header_color_scheme: premade.header_color_scheme,
        header_color: premade.header_color,
        header_gradient: premade.header_gradient ?? null,
        title_color: premade.title_color,
        description_color: premade.description_color,
        button_color: premade.button_color,
        button_text_color: premade.button_text_color,
        player_controls_color: premade.player_controls_color,
      };
    }
  }

  return {};
}

export function enrichMusicLinkDetail(row) {
  const base = enrichMusicLinkListRow(row);
  if (!base) {
    return base;
  }
  const fallback = mlPremadeColorsData[0] || {};
  const colors = resolveMusicLinkColorSource(base);
  const detail = {
    ...base,
    source: base.source ?? 1,
    ean: base.ean ?? "",
    preview_files: Array.isArray(base.preview_files) ? base.preview_files : [],
    preview_files_missing:
      base.preview_files_missing ?? !(Array.isArray(base.preview_files) && base.preview_files.length),
    color_scheme: Number(base.color_scheme) || 1,
    premade_color:
      Number(base.color_scheme) === 2
        ? null
        : base.premade_color ?? colors.id ?? fallback.id ?? 1,
    header_color_scheme:
      colors.header_color_scheme ?? base.header_color_scheme ?? fallback.header_color_scheme ?? 1,
    header_color: colors.header_color ?? fallback.header_color ?? "#ffffff",
    header_gradient: colors.header_gradient ?? base.header_gradient ?? fallback.header_gradient ?? null,
    title_color: colors.title_color ?? fallback.title_color ?? "#ffffff",
    description_color: colors.description_color ?? fallback.description_color ?? "#ffffff",
    button_color: colors.button_color ?? fallback.button_color ?? "#000a60",
    button_text_color: colors.button_text_color ?? fallback.button_text_color ?? "#ffffff",
    player_controls_color:
      colors.player_controls_color ?? fallback.player_controls_color ?? "#ffffff",
    button_style: Number(base.button_style) || 1,
    custom_color: base.custom_color ?? null,
  };

  STORE_KEYS.forEach(storeKey => {
    const field = `${storeKey}_url`;
    if (detail[field] == null) {
      detail[field] = base[field] || "";
    }
  });

  if (!detail.spotify_url) {
    detail.spotify_url = "https://open.spotify.com/album/00000000000000000000";
  }

  return detail;
}

const SEED_MUSIC_LINK_CREATED_BY_ID = new Map(
  mockMusicLinksData
    .filter(row => row?.id != null && row.created)
    .map(row => [String(row.id), row.created]),
);

export function getMusicLinkRows() {
  // Seed first, session last — user add/edit overrides mock seed for the same id.
  const merged = mergeById(
    getActiveDemoMusicLinkSeedRows(mockMusicLinksData).map(enrichMusicLinkListRow),
    getDemoMusicLinkRows().map(enrichMusicLinkListRow),
  );
  return merged.map(row => {
    const seedCreated = SEED_MUSIC_LINK_CREATED_BY_ID.get(String(row.id));
    return seedCreated ? { ...row, created: seedCreated } : row;
  });
}

export function getMusicLinkById(id) {
  const row = getMusicLinkRows().find(item => String(item.id) === String(id));
  return row ? enrichMusicLinkDetail(row) : { detail: "Not found." };
}

export function findMusicLinkByPublicParts(urlPart1, urlPart2) {
  const slug = String(urlPart1 || "").trim().toLowerCase();
  const part2 = String(urlPart2 || "").trim().toLowerCase();
  const row = getMusicLinkRows().find(item => {
    const rowPart2 = String(item.url_part2 || "").trim().toLowerCase();
    const rowSlug = String(item.url_part1_name || "").trim().toLowerCase();
    const rowPartId = String(item.url_part1 || "").trim().toLowerCase();
    const part1Match = rowSlug === slug || rowPartId === slug;
    return part1Match && rowPart2 === part2;
  });
  return row ? enrichMusicLinkDetail(row) : undefined;
}

export function buildMusicLinkPublicUrl(row) {
  const enriched = enrichMusicLinkListRow(row);
  const slug = enriched.url_part1_name || resolveUrlPartFromSlug(enriched.url_part1)?.part || "demo";
  const part2 = enriched.url_part2 || enriched.id;
  const base =
    (typeof process !== "undefined" && process.env.REACT_APP_MUSIC_LINK_URL) ||
    "https://musiclink.io";
  return `${String(base).replace(/\/+$/, "")}/l/${slug}/${part2}`;
}

function serializeImageField(value) {
  if (value == null || value === "") {
    return value;
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "object" && value.default) {
    return value.default;
  }
  return String(value);
}

/** JSON-safe rows for clipboard export (e.g. updating musicLinksData.js). */
export function getMusicLinkMockExportPayload() {
  return getMusicLinkRows().map(row => {
    const { musiclink_url, ...rest } = row || {};
    return {
      ...rest,
      image: serializeImageField(rest.image),
      image_small: serializeImageField(rest.image_small),
      image_big: serializeImageField(rest.image_big),
    };
  });
}

export function buildMusicLinkListRowFromPayload(body, id) {
  const payload = body || {};
  const urlPart = mockMusicLinkUrlPartsData.find(
    part => String(part.id) === String(payload.url_part1),
  );
  const slug = urlPart?.part || payload.url_part1_name || "demo";
  const urlPart2 = payload.url_part2 || `ml-${id}`;
  const image = payload.image_small || payload.image_big || payload.image || "";
  const row = {
    ...payload,
    ...materializeMusicLinkColors(payload, id),
    id: Number(id) || id,
    release_name: payload.release_name || "",
    artist_name: payload.artist_name || "",
    image,
    image_small: payload.image_small || image,
    image_big: payload.image_big || image,
    url_part1: payload.url_part1,
    url_part1_name: urlPart?.part || slug,
    url_part2: urlPart2,
    url: buildMusicLinkPublicUrl({
      url_part1: payload.url_part1,
      url_part1_name: slug,
      url_part2: urlPart2,
      id,
    }),
    created: payload.created || new Date().toISOString(),
    owner: payload.owner || "John Doe",
  };
  return enrichMusicLinkDetail(row);
}
